import React, {useMemo} from 'react'
import {getMyOrderPreferences_preferences} from '@data/queries/preferences/__generated__/getMyOrderPreferences'
import {getWebsiteMenuInformation_cached_website} from '@data/queries/website/__generated__/getWebsiteMenuInformation_cached'
import hasWebpSupport from '@helpers/misc/hasWebpSupport'
import useWebsiteId from '@hooks/useWebsiteId'

import {getCategoriesAndProductsCPGsWeb_cached_categories} from './__generated__/getCategoriesAndProductsCPGsWeb_cached'
import {getCategoriesAndProductsWeb_cached_categories} from './__generated__/getCategoriesAndProductsWeb_cached'
import ProductsContent from './ProductsContent'
import useCategoriesAndProducts from './useCategoriesAndProducts'
import useSetOutOfStockProducts from './useSetOutOfStockProducts'

export interface ProductsProps {
  menuId: string
  storeId: string
  deliverAt: string
  productsLayout: string
  layout: any
  loading?: boolean
  nextOpenDate: JSX.Element
  preferences: getMyOrderPreferences_preferences
  productCardDesign: string
  website: getWebsiteMenuInformation_cached_website

  /**
   * If included, will use to prerender on serverside. Note: These queries are big, and are currently causing performance issues for sites with a lot of products.
   * We disabled them temporarily (by removing it from props in pages/order/index).
   */
  preloadedCategories?:
    | getCategoriesAndProductsWeb_cached_categories
    | getCategoriesAndProductsCPGsWeb_cached_categories
}

export default function Products(props: ProductsProps) {
  const {menuId, storeId, deliverAt, preloadedCategories, website} = props
  const websiteId = useWebsiteId()
  const format = hasWebpSupport() ? 'webp' : 'jpeg'
  const {categories} = useCategoriesAndProducts({
    websiteId,
    menuId,
    format,
    preloadedCategories
  })

  const {outOfStockIds} = useSetOutOfStockProducts({websiteId, categories, storeId, deliverAt})

  const memoizedCategories = useMemo(() => {
    if (!(categories?.items && outOfStockIds)) return categories
    const newCategories = {items: []}
    newCategories.items = categories.items.map(category => {
      const newProducts = category.products.map(product => {
        return {...product, isOutOfStock: outOfStockIds.includes(product._id)}
      })

      return {...category, products: newProducts}
    })

    return newCategories
  }, [outOfStockIds, categories])

  return <ProductsContent {...props} website={website} categories={memoizedCategories} />
}
